/**
 * Try On page: upload a photo (or take a selfie) and try our colours on it.
 *   1. Upload -> the photo is shown and the currently selected shade is sent to
 *      the backend.
 *   2. The completed result is retained only in this page's memory, and the
 *      browser acknowledges delivery so the server deletes temporary files.
 *   3. Already rendered shades are reused from the page's memory.
 *   4. "Zamów w tym kolorze" -> shop product + ?odcien=<code> (PDP pre-selects it).
 *
 * If the backend has no API configured, its health check fails, or the job
 * errors, the preview falls back to the original photo and the UI says the
 * colour preview is temporarily unavailable — the order flow still works.
 *
 * Colour *recommendation* lives on a separate page (Dobór koloru); this page is
 * purely the try-on, so there is no auto-analysis here.
 */
const initializedVivaTryon = new WeakSet();
const initVivaTryon = () => {
  const root = document.querySelector('.viva-tryon');
  if (!root) return;
  if (initializedVivaTryon.has(root)) return;
  initializedVivaTryon.add(root);
  let content = {};
  try { content = JSON.parse(root.dataset.toolContent || '{}'); } catch (error) { content = {}; }

  const dropzone = root.querySelector('[data-tryon-dropzone]');
  const preview = root.querySelector('[data-tryon-preview]');
  const previewNote = root.querySelector('[data-tryon-preview-note]');
  const photo = root.querySelector('[data-tryon-photo]');
  const overlayPhoto = root.querySelector('[data-tryon-photo-overlay]');
  const compareSlider = root.querySelector('[data-tryon-compare-slider]');
  const compareDivider = root.querySelector('[data-tryon-compare-divider]');
  const beforeLabel = root.querySelector('[data-tryon-before-label]');
  const afterLabel = root.querySelector('[data-tryon-after-label]');
  const compareCode = root.querySelector('[data-tryon-compare-code]');
  const stageActions = root.querySelector('[data-tryon-stage-actions]');
  const changeButtons = Array.from(root.querySelectorAll('[data-tryon-change]'));
  const status = root.querySelector('[data-tryon-status]');
  const progressRoot = root.querySelector('[data-tryon-progress]');
  const progressElapsed = root.querySelector('[data-tryon-progress-elapsed]');
  const progressBar = root.querySelector('[data-tryon-progress-bar]');
  const progressLabel = root.querySelector('[data-tryon-progress-label]');
  const paletteList = root.querySelector('[data-tryon-suggest-list]');
  const picker = root.querySelector('[data-tryon-picker]');
  const palette = window.VivaToolPalette;
  const shadeButtons = Array.from(root.querySelectorAll('[data-tryon-shade]'));
  // Registry rows already follow the shared order saved in VIVA Catalog.
  // Keep this order when filtering instead of overriding it in the browser.
  const groupButtons = Array.from(root.querySelectorAll('[data-tryon-group]'));
  const selectedLabel = root.querySelector('[data-tryon-selected]');
  let paletteGroup = 'all';
  const showGroup = group => {
    paletteGroup = group;
    shadeButtons.forEach(button => {
      const item = button.closest('li');
      if (item) item.hidden = button.dataset.productExcluded === 'true' || (group !== 'all' && (palette?.group(button.dataset.code) || (button.dataset.code.includes('/') ? 'mixed' : 'solid')) !== group);
    });
    groupButtons.forEach(button => button.setAttribute('aria-pressed', String(button.dataset.tryonGroup === group)));
  };
  groupButtons.forEach(button => button.addEventListener('click', () => showGroup(button.dataset.tryonGroup)));
  const typesBlock = root.querySelector('[data-tryon-types]');
  const typeButtons = Array.from(root.querySelectorAll('[data-tryon-type]'));
  const orderBtn = root.querySelector('[data-tryon-order]');
  const backBtn = root.querySelector('[data-tryon-back]');
  const selfieBtn = root.querySelector('[data-tryon-selfie]');
  const uploadInput = root.querySelector('[data-tryon-upload]');
  const notice = root.querySelector('[data-tryon-notice]');
  const camera = root.querySelector('[data-tryon-camera]');
  const video = root.querySelector('[data-tryon-video]');
  const captureBtn = root.querySelector('[data-tryon-capture]');
  const cancelBtn = root.querySelector('[data-tryon-cancel]');

  if (!(photo instanceof HTMLImageElement) || !(overlayPhoto instanceof HTMLImageElement) || !paletteList) return;

  const initialType = typeButtons.find((b) => b.classList.contains('is-selected')) || typeButtons[0];
  let selectedType = initialType;
  let selectedCode = null;
  let stream = null;

  // --- Live recolour preview (batch try-on backend), with graceful fallback ---
  const apiUrl = (root.dataset.tryonApi || '').trim().replace(/\/$/, '');
  let recolorByCode = {}; // shade code -> browser-local recoloured image URL
  let recolorState = 'idle'; // idle | loading | ready | unavailable
  let originalPhotoSrc = '';
  let activeShadeJob = null;
  let queuedShadeCode = null;
  let tryonStateVersion = 0;
  let comparePos = 54;
  const progress =
    window.VivaAsyncProgress && progressRoot
      ? window.VivaAsyncProgress.create({
          root: progressRoot,
          barEl: progressBar,
          labelEl: progressLabel,
          elapsedEl: progressElapsed,
          doneLabel: content.progress_done,
          stageLabels: { preflight: content.progress_preflight, uploading: content.progress_uploading, queued: content.progress_queued, running: content.progress_running, segmenting_hair: content.progress_running, matching_reference: content.progress_running, fallback: content.progress_running },
          stageCaps: {
            preflight: 0.15,
            uploading: 0.35,
            queued: 0.45,
            running: 0.9,
          },
        })
      : null;

  // Product mode: arrived from a PDP (?product=<handle>) — show that product's
  // colours and a "back to product" button instead of the generic order flow.
  const productParam = new URLSearchParams(window.location.search).get('product');
  let productCtx = null;
  try {
    const stored = JSON.parse(localStorage.getItem('viva:tryon-context'));
    if (stored && productParam && stored.handle === productParam) productCtx = stored;
  } catch (error) {
    productCtx = null;
  }
  const productBack = productCtx ? productCtx.url || '/products/' + productCtx.handle : '';

  if (productCtx) {
    // Keep only the colours this product offers; hide the type picker + order CTA.
    const allowed = new Set((productCtx.shades || []).map(String));
    shadeButtons.forEach((btn) => {
      if (!allowed.has(String(btn.dataset.code))) {
        const li = btn.closest('li');
        btn.dataset.productExcluded = 'true';
        if (li) li.hidden = true;
      }
    });
    if (typesBlock) typesBlock.remove();
    if (orderBtn) orderBtn.remove();
  } else if (backBtn) {
    backBtn.remove();
  }

  const setNotice = (message) => {
    if (!notice) return;
    notice.textContent = message || '';
    notice.hidden = !message;
  };

  // --- CTA: "back to product" (product mode) or "order in this colour" (general) ---
  const updateCta = () => {
    if (!selectedCode) return;
    try {
      localStorage.setItem('viva:selected-odcien', selectedCode);
    } catch (error) {
      /* storage may be unavailable */
    }
    if (productCtx) {
      if (backBtn) {
        backBtn.href = palette?.productTarget(productBack, selectedCode, window.location.origin) || productBack;
        backBtn.hidden = false;
      }
      return;
    }
    if (!orderBtn) return;
    const productUrl = selectedType?.dataset.url || '';
    const target = palette?.productTarget(productUrl, selectedCode, window.location.origin);
    orderBtn.href = target || orderBtn.dataset.shopBase;
    orderBtn.hidden = !target;
  };

  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  // fetch that aborts instead of hanging when the backend is unreachable.
  const fetchWithTimeout = (url, options, ms) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), ms);
    return fetch(url, Object.assign({ cache: 'no-store' }, options, { signal: controller.signal })).finally(() => clearTimeout(timer));
  };

  const acknowledgeJob = async (jobId) => {
    for (let attempt = 0; attempt < 3; attempt += 1) {
      try {
        const response = await fetchWithTimeout(apiUrl + '/api/public/try-on/jobs/' + jobId + '/ack', { method: 'POST', keepalive: true }, 7000);
        if (response.ok) return;
      } catch (error) { /* the independent expiry remains the fallback */ }
      await sleep(500 * (attempt + 1));
    }
  };

  const receiveResult = async (url) => {
    for (let attempt = 0; attempt < 2; attempt += 1) {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 20000);
      try {
        const response = await fetch(url, { cache: 'no-store', headers: { Accept: 'image/*' }, signal: controller.signal });
        if (!response.ok) throw new Error('image-' + response.status);
        // Keep the timeout active until every byte has arrived, not just headers.
        return await response.blob();
      } catch (error) {
        if (attempt === 1) throw error;
      } finally { clearTimeout(timer); }
    }
  };

  const setStatus = (text) => {
    if (status && text) {
      status.hidden = false;
      status.textContent = text;
    }
  };

  const applyComparePos = (value) => {
    comparePos = Math.min(100, Math.max(0, Number.isFinite(value) ? value : 54));
    if (preview instanceof HTMLElement) {
      preview.style.setProperty('--tryon-compare-pos', `${comparePos.toFixed(2)}%`);
    }
    if (compareSlider instanceof HTMLInputElement && document.activeElement !== compareSlider) {
      compareSlider.value = String(Math.round(comparePos));
    }
  };

  const setCompareActive = (active, code) => {
    if (!(preview instanceof HTMLElement)) return;
    preview.classList.toggle('is-compare-active', active);
    overlayPhoto.hidden = !active;
    if (compareSlider instanceof HTMLInputElement) compareSlider.hidden = !active;
    if (compareDivider instanceof HTMLElement) compareDivider.hidden = !active;
    if (beforeLabel instanceof HTMLElement) beforeLabel.hidden = !active;
    if (afterLabel instanceof HTMLElement) afterLabel.hidden = !active;
    if (compareCode instanceof HTMLElement) compareCode.textContent = active && code ? code : '';
    if (!active) {
      overlayPhoto.removeAttribute('src');
    }
  };

  // Busy indicator = the VI|VA loader over the preview. The textual progress
  // (stage + elapsed + bar) lives in the progress component, so we don't also
  // spin the status line — that would be three overlapping indicators.
  const setBusy = (on) => {
    if (preview) preview.classList.toggle('is-busy', on);
  };

  const loadImage = (src) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('image-load-failed'));
      img.src = src;
    });

  // Web-sized JPEG blob of the chosen photo (the backend caps resolution too).
  const imageToBlob = (img) =>
    new Promise((resolve, reject) => {
      const max = 1024;
      const width = img.naturalWidth;
      const height = img.naturalHeight;
      if (!width || !height) {
        reject(new Error('no-image'));
        return;
      }
      const scale = Math.min(1, max / Math.max(width, height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(width * scale));
      canvas.height = Math.max(1, Math.round(height * scale));
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('no-context'));
        return;
      }
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('no-blob'))), 'image/jpeg', 0.9);
    });

  // Build the {code, reference_url} palette from the visible shade buttons.
  const selectedShadeButton = () => shadeButtons.find((button) => button.classList.contains('is-selected')) || null;

  const findShadeButton = (code) => shadeButtons.find((button) => String(button.dataset.code) === String(code)) || null;

  // Keep the on-photo note minimal: the colour is already shown in the grid and
  // the compare label, and the loading/unavailable state is in the ring / status
  // line — so the note only labels the photo (+ a drag hint while comparing).
  const renderSelectedPreview = () => {
    const selected = selectedShadeButton();
    const code = selected ? selected.dataset.code : null;

    if (recolorState === 'ready' && code && recolorByCode[code]) {
      photo.src = originalPhotoSrc;
      overlayPhoto.src = recolorByCode[code];
      setCompareActive(true, code);
      if (previewNote) previewNote.textContent = content.compare_hint;
      return;
    }
    setCompareActive(false, null);
    if (originalPhotoSrc) photo.src = originalPhotoSrc;
    if (previewNote) previewNote.textContent = content.photo_label;
  };

  const resetTryonCache = () => {
    tryonStateVersion += 1;
    Object.values(recolorByCode).forEach((url) => {
      if (typeof url === 'string' && url.indexOf('blob:') === 0) URL.revokeObjectURL(url);
    });
    recolorByCode = {};
    recolorState = 'idle';
    activeShadeJob = null;
    queuedShadeCode = null;
  };

  const markRecolorUnavailable = (message) => {
    recolorState = 'unavailable';
    activeShadeJob = null;
    queuedShadeCode = null;
    setBusy(false);
    if (progress) progress.reset();
    setStatus(message || content.unavailable_status);
    renderSelectedPreview();
  };

  const onShadeJobSettled = () => {
    const nextCode = queuedShadeCode;
    queuedShadeCode = null;
    if (!nextCode || recolorByCode[nextCode] || !originalPhotoSrc) return;
    const nextButton = findShadeButton(nextCode);
    if (nextButton) {
      void requestShadePreview(nextButton);
    }
  };

  // Each new shade uploads the in-memory photo; received renders stay local.
  const requestShadePreview = async (button) => {
    const code = (button && button.dataset.code) || '';
    const referenceUrl = (button && button.dataset.referenceUrl) || '';
    if (!code) return;
    if (recolorByCode[code]) {
      recolorState = 'ready';
      activeShadeJob = null;
      queuedShadeCode = null;
      setBusy(false);
      if (progress) progress.reset();
      renderSelectedPreview();
      return;
    }
    if (!apiUrl || !referenceUrl || !originalPhotoSrc) {
      markRecolorUnavailable();
      return;
    }
    if (activeShadeJob) {
      if (activeShadeJob.code !== code) {
        queuedShadeCode = code;
        setStatus(content.queued_status);
      }
      return;
    }

    recolorState = 'loading';
    activeShadeJob = { code };
    const requestVersion = tryonStateVersion;
    setBusy(true);
    if (progress) progress.start('preflight');
    setCompareActive(false, null);
    renderSelectedPreview();
    setStatus(content.loading_status);

    try {
      const ready = await fetchWithTimeout(apiUrl + '/health/ready', { headers: { Accept: 'application/json' } }, 6000)
        .then((response) => response.ok)
        .catch(() => false);
      if (!ready) {
        markRecolorUnavailable();
        return;
      }

      if (progress) progress.update('uploading');
      const form = new FormData();
      const blob = await imageToBlob(await loadImage(originalPhotoSrc));
      form.append('source_image', blob, 'photo.jpg');
      form.append('reference_url', referenceUrl);
      // Every Shopify shade image is a full-frame swatch. The API must not try
      // to find a head/hair silhouette in this square texture.
      form.append('reference_mode', 'full_frame');
      form.append('shade_code', code);

      const created = await fetchWithTimeout(apiUrl + '/api/public/try-on/jobs', { method: 'POST', body: form }, 20000);
      if (requestVersion !== tryonStateVersion) return;
      if (!created.ok) {
        markRecolorUnavailable();
        return;
      }
      const job = await created.json();
      const jobId = job.job_id;
      if (!jobId) {
        markRecolorUnavailable();
        return;
      }

      if (progress) progress.update('running');
      const deadline = Date.now() + 90000;
      let pollErrors = 0;
      while (Date.now() < deadline) {
        await sleep(1100);
        let data;
        try {
          const response = await fetchWithTimeout(apiUrl + '/api/public/try-on/jobs/' + jobId, { headers: { Accept: 'application/json' } }, 7000);
          if (!response.ok) throw new Error('status-' + response.status);
          data = await response.json();
        } catch (error) {
          pollErrors += 1;
          if (pollErrors >= 3) {
            markRecolorUnavailable();
            return;
          }
          continue;
        }
        if (requestVersion !== tryonStateVersion) return;
        pollErrors = 0;
        const statusData = data.status || {};
        const state = statusData.state;
        if (state === 'queued' || state === 'running') {
          if (progress) progress.update(state);
        }
        if (state === 'succeeded') {
          const output = (data.output || {}).result || {};
          const artifactId = output.artifact_id;
          const artifact = (data.artifacts || []).find((item) => item.artifact_id === artifactId) || (data.artifacts || [])[0];
          const artifactUrl = artifact && artifact.url;
          if (!artifactUrl) {
            markRecolorUnavailable();
            return;
          }
          // Keep the complete Blob in this page before acknowledging delivery.
          // A failed download can be retried until the independent expiry runs.
          const deliveryUrl = new URL(artifactUrl, apiUrl);
          deliveryUrl.searchParams.set('delivery', 'ack');
          const resultBlob = await receiveResult(deliveryUrl.href);
          // Receiving all bytes is sufficient even if the user changed photos.
          void acknowledgeJob(jobId);
          if (requestVersion !== tryonStateVersion) return;
          recolorByCode[code] = URL.createObjectURL(resultBlob);
          recolorState = 'ready';
          activeShadeJob = null;
          setBusy(false);
          if (progress) progress.finish();
          setStatus(content.ready_status);
          renderSelectedPreview();
          window.VivaAnalytics?.publish('try_on_completed', { shade_code: code });
          onShadeJobSettled();
          return;
        }
        if (state === 'failed' || state === 'rejected') {
          void acknowledgeJob(jobId);
          const output = (data.output || {}).result || {};
          if (output && output.code && output.cached && state === 'rejected') {
            markRecolorUnavailable(content.failed_status);
            return;
          }
          markRecolorUnavailable();
          return;
        }
      }
      markRecolorUnavailable();
    } catch (error) {
      markRecolorUnavailable();
    }
  };

  const showPreview = () => {
    renderSelectedPreview();
  };

  const selectShade = (button) => {
    selectedCode = button.dataset.code || null;
    if (selectedLabel) selectedLabel.textContent = [selectedCode, button.dataset.name].filter(Boolean).join(' · ');
    if (paletteGroup !== 'all' && palette?.group(selectedCode) !== paletteGroup) showGroup(palette.group(selectedCode));
    shadeButtons.forEach((b) => {
      const isOn = b === button;
      b.classList.toggle('is-selected', isOn);
      b.setAttribute('aria-pressed', isOn ? 'true' : 'false');
    });
    if (selectedCode && recolorByCode[selectedCode]) {
      queuedShadeCode = null;
      recolorState = 'ready';
      showPreview();
    } else {
      showPreview();
      if (originalPhotoSrc) {
        void requestShadePreview(button);
      }
    }
    updateCta();
  };

  const selectType = (button) => {
    selectedType = button;
    typeButtons.forEach((b) => {
      const isOn = b === button;
      b.classList.toggle('is-selected', isOn);
      b.setAttribute('aria-pressed', isOn ? 'true' : 'false');
    });
    updateCta();
  };

  shadeButtons.forEach((button) => button.addEventListener('click', () => selectShade(button)));
  typeButtons.forEach((button) => button.addEventListener('click', () => selectType(button)));

  // --- Stage switching (only the photo box swaps; colours stay visible) ---
  const showStage = (hasPhoto) => {
    if (dropzone) dropzone.hidden = hasPhoto;
    if (preview) preview.hidden = !hasPhoto;
    if (stageActions) stageActions.hidden = !hasPhoto;
    changeButtons.forEach((button) => {
      button.hidden = !hasPhoto;
    });
  };

  photo.addEventListener('load', () => window.VivaPhotoSession?.save(photo));

  const loadPhoto = (src) => {
    resetTryonCache();
    setNotice('');
    originalPhotoSrc = src;
    photo.src = src;
    setCompareActive(false, null);
    applyComparePos(54);
    showStage(true);
    if (picker) picker.hidden = false; // reveal the colour picker now there's a photo
    renderSelectedPreview();
    updateCta(); // a photo is now loaded — the order CTA may appear
    setStatus(content.choose_status);
    const selected = selectedShadeButton();
    if (selected) {
      void requestShadePreview(selected);
    }
  };

  const showUpload = () => {
    window.VivaPhotoSession?.clear();
    closeCamera();
    resetTryonCache();
    showStage(false);
    if (picker) picker.hidden = false;
    if (uploadInput) uploadInput.value = '';
    photo.removeAttribute('src');
    overlayPhoto.removeAttribute('src');
    originalPhotoSrc = '';
    if (progress) progress.reset();
    setCompareActive(false, null);
    updateCta(); // no photo — pull the order CTA back out of reach
    if (status) {
      status.hidden = false;
      status.textContent = content.initial_status;
    }
  };

  // --- Camera ---
  const stopStream = () => {
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      stream = null;
    }
    if (video instanceof HTMLVideoElement) video.srcObject = null;
  };

  function closeCamera() {
    stopStream();
    if (camera) camera.hidden = true;
  }

  const supportsCamera = !!(navigator.mediaDevices && typeof navigator.mediaDevices.getUserMedia === 'function');

  const startCamera = async () => {
    if (!camera || !(video instanceof HTMLVideoElement)) return;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' }, audio: false });
      video.srcObject = stream;
      camera.hidden = false;
      await video.play().catch(() => {});
    } catch (error) {
      camera.hidden = true;
      setNotice(content.camera_error);
    }
  };

  const captureFrame = () => {
    if (!(video instanceof HTMLVideoElement) || !video.videoWidth) return;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.translate(canvas.width, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const data = canvas.toDataURL('image/jpeg', 0.92);
    closeCamera();
    loadPhoto(data);
  };

  // --- Inputs ---
  const readFile = (file) => {
    if (!file || !file.type.startsWith('image/')) {
      setNotice(content.file_error);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') loadPhoto(reader.result);
    };
    reader.readAsDataURL(file);
  };

  if (uploadInput instanceof HTMLInputElement) {
    uploadInput.addEventListener('change', () => {
      readFile(uploadInput.files && uploadInput.files[0]);
      uploadInput.value = '';
    });
  }

  if (selfieBtn) {
    if (supportsCamera) {
      selfieBtn.addEventListener('click', startCamera);
    } else if (uploadInput instanceof HTMLInputElement) {
      selfieBtn.addEventListener('click', () => {
        uploadInput.setAttribute('capture', 'user');
        uploadInput.click();
      });
    }
  }

  // --- Drag & drop onto the dropzone ---
  if (dropzone) {
    ['dragenter', 'dragover'].forEach((type) =>
      dropzone.addEventListener(type, (event) => {
        event.preventDefault();
        dropzone.classList.add('is-dragover');
      })
    );
    ['dragleave', 'dragend', 'drop'].forEach((type) =>
      dropzone.addEventListener(type, () => dropzone.classList.remove('is-dragover'))
    );
    dropzone.addEventListener('drop', (event) => {
      event.preventDefault();
      const file = event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files[0];
      if (file) readFile(file);
    });
  }

  if (captureBtn) captureBtn.addEventListener('click', captureFrame);
  if (cancelBtn) cancelBtn.addEventListener('click', closeCamera);
  changeButtons.forEach((button) => button.addEventListener('click', showUpload));
  if (compareSlider instanceof HTMLInputElement) {
    compareSlider.addEventListener('input', () => {
      applyComparePos(parseFloat(compareSlider.value));
    });
  }
  applyComparePos(comparePos);

  // Decide the initially-selected colour: PDP shade > Dobór match > first available.
  let presetButton = null;
  if (productCtx && productCtx.selected) {
    presetButton = shadeButtons.find((b) => String(b.dataset.code) === String(productCtx.selected));
  }
  if (!presetButton) {
    let carriedShade = null;
    try {
      carriedShade = sessionStorage.getItem('viva:tryon-shade');
      if (carriedShade) sessionStorage.removeItem('viva:tryon-shade');
    } catch (error) {
      carriedShade = null;
    }
    if (carriedShade) {
      presetButton = shadeButtons.find((b) => String(b.dataset.code) === String(carriedShade));
    }
  }
  if (!presetButton) presetButton = shadeButtons.find(button => button.dataset.productExcluded !== 'true') || null;
  showGroup('all');
  if (presetButton) selectShade(presetButton);
  groupButtons.forEach(button => {
    if (button.dataset.tryonGroup === 'all') return;
    const hasShades = shadeButtons.some(shade => shade.dataset.productExcluded !== 'true' && (palette?.group(shade.dataset.code) || (shade.dataset.code.includes('/') ? 'mixed' : 'solid')) === button.dataset.tryonGroup);
    button.hidden = !hasShades;
  });

  window.addEventListener('pagehide', () => { closeCamera(); resetTryonCache(); });
  const restorePhoto = () => {
    const saved = window.VivaPhotoSession?.read();
    if (saved && saved !== originalPhotoSrc) loadPhoto(saved);
  };
  restorePhoto();
  window.addEventListener('pageshow', event => { if (event.persisted) restorePhoto(); });

};
document.addEventListener('DOMContentLoaded', initVivaTryon);
document.addEventListener('shopify:section:load', initVivaTryon);
